import { useEffect, useRef, useState } from "react";
import { Badge, Button, Card, CardBody, Col, Row } from "reactstrap";
import WizardPanel from "../WizardPanel";
import { PanelField } from "../panelFields";
import { AsyncButton } from "../../../common/buttons";
import { isDashcamRequestCancelled } from "../../../../services/capabilitiesService";
import {
  dashcamProbeBlocker,
  installationProbeFingerprint,
  probeInstallationViaDashcam,
} from "../shared/probeInstallationViaDashcam";
import { manualScanBlocker, scanInstallationChannels } from "../shared/scanInstallationChannels";
import { captureWizardError } from "../shared/wizardObservability";

const probeKey = (dvr) => `probeResult:${dvr.id}`;

/** Dashcam probe + manual fallback for a single DVR. */
function DvrRtspSection({ installation, dvr, index, setDvrField, draftFields, setDraftField }) {
  const [dashcamError, setDashcamError] = useState(null);
  const [manualResult, setManualResult] = useState(null);
  const [manualTestOpen, setManualTestOpen] = useState(false);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const setIfMounted =
    (fn) =>
    (...args) => {
      if (mountedRef.current) fn(...args);
    };

  const fingerprint = installationProbeFingerprint(installation, dvr);
  const key = probeKey(dvr);
  const rawProbe = draftFields?.[key] ?? null;
  const probeResult = rawProbe && rawProbe.fingerprint === fingerprint ? rawProbe : null;
  const dashcamBlock = dashcamProbeBlocker(installation, dvr);
  const showManualFallback = Boolean(dashcamError) || manualTestOpen;
  const manualBlock = showManualFallback ? manualScanBlocker(installation, dvr) : null;

  const handleDashcamProbe = async () => {
    setDashcamError(null);
    setManualResult(null);
    setDraftField(key, null);
    try {
      const { channels, raw, suffixTemplate } = await probeInstallationViaDashcam({
        installation,
        dvr,
        inferenceHostname: installation?.server_name ?? null,
      });
      setDraftField(key, { source: "dashcam", channels, raw, fingerprint });
      if (suffixTemplate && !dvr?.rtsp_url_suffix) {
        setDvrField(dvr.id, "rtsp_url_suffix", suffixTemplate);
      }
    } catch (err) {
      if (isDashcamRequestCancelled(err)) return;
      captureWizardError(err, "dashcamProbe", {
        identifier: installation?.uid ?? installation?.deal_id,
        dvrId: dvr.id,
      });
      setIfMounted(setDashcamError)(err?.message ?? "Dashcam probe failed.");
    }
  };

  const handleManualProbe = async () => {
    setManualResult(null);
    try {
      const result = await scanInstallationChannels({ installation, dvr, channelNumbers: [1] });
      setIfMounted(setManualResult)({ kind: "ok", ...result });
    } catch (err) {
      setIfMounted(setManualResult)({ kind: "error", message: err?.message ?? "Probe failed." });
    }
  };

  return (
    <>
      <Card className="mb-3">
        <CardBody>
          <div className="d-flex align-items-center mb-2" style={{ gap: "0.75rem" }}>
            <h6 className="mb-0">DVR {index + 1} — probe with Dashcam</h6>
            {probeResult?.source === "dashcam" && (
              <Badge color="success" pill>
                {probeResult.channels?.length ?? 0} channels detected
              </Badge>
            )}
          </div>
          <small className="text-muted d-block mb-2">
            Tries Dashcam directly — auto-fills the RTSP URL suffix when it can derive one.
          </small>
          {probeResult?.source === "dashcam" && dvr?.rtsp_url_suffix && (
            <small className="text-muted d-block mb-2">
              Suffix: <code>{dvr.rtsp_url_suffix}</code>
            </small>
          )}
          <div className="d-flex align-items-center" style={{ gap: "0.6rem" }}>
            <AsyncButton color="primary" outline disabled={Boolean(dashcamBlock)} onClick={handleDashcamProbe}>
              <i className="fas fa-play mr-2" />
              Probe with Dashcam
            </AsyncButton>
            {!showManualFallback && (
              <Button color="link" className="p-0" onClick={() => setManualTestOpen(true)}>
                Test an RTSP suffix manually
              </Button>
            )}
            {dashcamBlock && <small className="text-muted">{dashcamBlock}</small>}
            {dashcamError && (
              <small className="text-danger">
                <i className="fas fa-times-circle mr-1" />
                {dashcamError}
              </small>
            )}
          </div>
        </CardBody>
      </Card>

      {showManualFallback && (
        <Card className="mb-3">
          <CardBody>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <h6 className="mb-0">
                DVR {index + 1} — {dashcamError ? "manual fallback" : "test an RTSP suffix"}
              </h6>
              {!dashcamError && manualTestOpen && (
                <Button color="link" size="sm" className="p-0" onClick={() => setManualTestOpen(false)}>
                  Hide
                </Button>
              )}
            </div>
            <small className="text-muted d-block mb-3">
              {dashcamError
                ? "Dashcam couldn't reach the DVR. Enter the RTSP URL suffix."
                : "Enter a suffix and check the stream — this probes channel 1 only, without re-running the full Dashcam probe."}
            </small>
            {dashcamError && (
              <div className="mb-3">
                <Badge color="info" pill>
                  DVR brand: {dvr?.dvr_brand?.trim() ? dvr.dvr_brand : "not recorded"}
                </Badge>
              </div>
            )}
            <Row>
              <Col md={12}>
                <PanelField
                  label="RTSP URL suffix"
                  placeholder="e.g. /Streaming/Channels/{}01"
                  mono
                  value={dvr?.rtsp_url_suffix}
                  onChange={(v) => setDvrField(dvr.id, "rtsp_url_suffix", v)}
                />
              </Col>
            </Row>
            <div className="d-flex align-items-center" style={{ gap: "0.6rem" }}>
              <AsyncButton color="primary" outline disabled={Boolean(manualBlock)} onClick={handleManualProbe}>
                <i className="fas fa-play mr-2" />
                Check stream
              </AsyncButton>
              {manualBlock && <small className="text-muted">{manualBlock}</small>}
              {manualResult?.kind === "error" && (
                <small className="text-danger">
                  <i className="fas fa-times-circle mr-1" />
                  {manualResult.message}
                </small>
              )}
              {manualResult?.kind === "ok" &&
                (manualResult.screenshots?.[0]?.[2] ? (
                  <small className="text-success">
                    <i className="fas fa-check-circle mr-1" />
                    Stream reachable
                  </small>
                ) : (
                  <small className="text-danger">
                    <i className="fas fa-times-circle mr-1" />
                    No frame returned for channel 1.
                  </small>
                ))}
            </div>
            {manualResult?.kind === "ok" && manualResult.screenshots?.[0]?.[2] && (
              <div className="mt-3">
                <img
                  src={manualResult.screenshots[0][2]}
                  alt="Channel 1 preview"
                  className="img-thumbnail"
                  style={{ maxWidth: "320px" }}
                />
              </div>
            )}
          </CardBody>
        </Card>
      )}
    </>
  );
}

/** @param {import("../WizardCard").StepPanelProps} props */
export default function RtspConfigPanel({ installation, dvrStore, draftFields, setDraftField, status, bottomNav }) {
  const { dvrs, setDvrField } = dvrStore;
  const persistedDvrs = (dvrs ?? []).filter((d) => d.id != null);

  return (
    <WizardPanel
      title="RTSP Configuration"
      subtitle="Check connectivity to each DVR; Dashcam first, manual fallback if it fails"
      status={status}
      bottomNav={bottomNav}
    >
      {persistedDvrs.length === 0 ? (
        <Card>
          <CardBody className="text-muted">Create a DVR on the Video Recorder step before probing.</CardBody>
        </Card>
      ) : (
        persistedDvrs.map((dvr, index) => (
          <DvrRtspSection
            key={dvr.id}
            installation={installation}
            dvr={dvr}
            index={index}
            setDvrField={setDvrField}
            draftFields={draftFields}
            setDraftField={setDraftField}
          />
        ))
      )}
    </WizardPanel>
  );
}
